import { useEffect } from 'react';

import { writeDesktopLog } from '@/lib/logger';
import { getSupabaseClient, supabase } from '@/lib/supabase/client';
import type { Tables } from '@/lib/supabase/types';
import { useSessionStore, type MembershipWithOrganization } from '@/state/session-store';

async function loadSessionProfile(userId: string) {
  const client = getSupabaseClient();
  const [profileResult, membershipsResult] = await Promise.all([
    client.from('profiles').select('*').eq('id', userId).maybeSingle(),
    client
      .from('memberships')
      .select('*, organization:organizations(*)')
      .eq('user_id', userId)
      .is('archived_at', null),
  ]);

  if (profileResult.error) {
    throw profileResult.error;
  }

  if (membershipsResult.error) {
    throw membershipsResult.error;
  }

  return {
    profile: profileResult.data as Tables<'profiles'> | null,
    memberships: (membershipsResult.data ?? []) as MembershipWithOrganization[],
  };
}

export function AuthBootstrap() {
  const hydrate = useSessionStore((state) => state.hydrate);
  const clear = useSessionStore((state) => state.clear);
  const setBootstrapped = useSessionStore((state) => state.setBootstrapped);

  useEffect(() => {
    if (!supabase) {
      setBootstrapped();
      return;
    }

    let disposed = false;

    async function bootstrap() {
      try {
        const {
          data: { session },
        } = await getSupabaseClient().auth.getSession();

        if (!session?.user) {
          if (!disposed) {
            clear();
          }
          return;
        }

        const { profile, memberships } = await loadSessionProfile(session.user.id);

        if (!disposed) {
          hydrate({
            bootstrapped: true,
            session,
            user: session.user,
            profile,
            memberships,
          });
        }
      } catch (error) {
        await writeDesktopLog('error', 'Failed to bootstrap auth session.', error);
        if (!disposed) {
          clear();
        }
      }
    }

    void bootstrap();

    const {
      data: { subscription },
    } = getSupabaseClient().auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        clear();
        return;
      }

      void loadSessionProfile(session.user.id)
        .then(({ profile, memberships }) => {
          if (!disposed) {
            hydrate({
              bootstrapped: true,
              session,
              user: session.user,
              profile,
              memberships,
            });
          }
        })
        .catch((error) => {
          void writeDesktopLog('error', 'Failed to refresh auth context.', error);
        });
    });

    return () => {
      disposed = true;
      subscription.unsubscribe();
    };
  }, [clear, hydrate, setBootstrapped]);

  return null;
}

